import axios from "axios";
import { uploadEnded, uploadStarted } from "@/lib/unsaved";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config._uploadId = config._uploadId || Symbol("upload");
    uploadStarted(config._uploadId);
  }
  return config;
});
api.interceptors.response.use(
  (r) => { if (r.config?._uploadId) uploadEnded(r.config._uploadId); return r; },
  (e) => { if (e.config?._uploadId) uploadEnded(e.config._uploadId); return Promise.reject(e); },
);

const MUTATING = ["post", "put", "patch", "delete"];
const CSRF_MSG = "Security token";
// Token issued by the server (login / MFA / GET /auth/me); used when the cookie is unreadable or stale.
let csrfToken = null;
// Bumped on every login / session check; a 401 from a request started under an older session (e.g. a presence beat
// still in flight at logout) must not sign out the new session.
let authGen = 0;

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

api.interceptors.request.use((config) => {
  config._gen = authGen;
  if (MUTATING.includes((config.method || "get").toLowerCase())) {
    const token = config._csrfRetry ? csrfToken : readCookie("cts_csrf") || csrfToken;
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    if (MUTATING.includes((r.config?.method || "").toLowerCase()) && r.config.url?.startsWith("/tickets")) window.dispatchEvent(new Event("cts:tickets-changed"));
    if (r.data && typeof r.data.csrf_token === "string") {
      if (r.config.url?.startsWith("/auth/login") || r.config.url?.startsWith("/auth/mfa")) authGen += 1;
      csrfToken = r.data.csrf_token;
    }
    return r;
  },
  async (error) => {
    const config = error.config || {};
    const url = config.url || "";
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && typeof detail === "string" && detail.startsWith(CSRF_MSG) && !config._csrfRetry) {
      try {
        const { data } = await api.get("/auth/me");
        csrfToken = data.csrf_token;
        return api({ ...config, _csrfRetry: true });
      } catch {
        /* fall through to the original error */
      }
    }
    if (error.response?.status === 401 && !url.startsWith("/auth/") && config._gen === authGen) {
      window.dispatchEvent(new CustomEvent("auth:expired", { detail: error.response?.data?.detail }));
    }
    if (error.response?.status === 403 && typeof detail === "string" && detail.startsWith("Set up two-factor")) window.dispatchEvent(new Event("auth:refresh"));
    return Promise.reject(error);
  }
);

const human = (k) => String(k).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const FRIENDLY = {
  missing: () => "is required",
  string_too_short: (x) => (x.ctx?.min_length > 1 ? `must be at least ${x.ctx.min_length} characters` : "can't be empty"),
  string_too_long: (x) => `must be at most ${x.ctx?.max_length} characters`,
  too_short: (x) => `needs at least ${x.ctx?.min_length} item(s)`,
  too_long: (x) => `can have at most ${x.ctx?.max_length} item(s)`,
  greater_than_equal: (x) => `must be at least ${x.ctx?.ge}`,
  greater_than: (x) => `must be more than ${x.ctx?.gt}`,
  less_than_equal: (x) => `must be at most ${x.ctx?.le}`,
  less_than: (x) => `must be less than ${x.ctx?.lt}`,
  int_parsing: () => "enter a whole number",
  int_from_float: () => "enter a whole number",
  float_parsing: () => "enter a number",
  bool_parsing: () => "must be yes or no",
  literal_error: (x) => `choose one of: ${x.ctx?.expected}`,
  enum: (x) => `choose one of: ${x.ctx?.expected}`,
  string_pattern_mismatch: () => "has an invalid format",
  string_type: () => "must be text",
  list_type: () => "must be a list",
};
const fieldOf = (x) => (x.loc || []).filter((p) => p !== "body" && p !== "query")[0];
const friendly = (x) => {
  if (/email/i.test(x.msg || "")) return "enter a valid email address";
  return (FRIENDLY[x.type] || (() => (x.msg || "is not valid").replace(/^Value error, /, "")))(x);
};

// 422 from FastAPI → { field: "friendly message" } for inline display (unexpected-field errors are not per-field).
export function fieldErrors(e) {
  const d = e?.response?.data?.detail;
  if (!Array.isArray(d)) return {};
  const out = {};
  d.filter((x) => x?.type !== "extra_forbidden" && fieldOf(x)).forEach((x) => { out[fieldOf(x)] ??= friendly(x); });
  return out;
}

function describe422(d, labels = {}) {
  const extras = d.filter((x) => x?.type === "extra_forbidden").map((x) => (x.loc || []).at(-1));
  const parts = d.filter((x) => x?.type !== "extra_forbidden").map((x) => (fieldOf(x) ? `${labels[fieldOf(x)] || human(fieldOf(x))}: ${friendly(x)}` : friendly(x)));
  if (extras.length) {
    console.error("Server rejected unexpected fields:", extras);
    parts.push("This page sent data the server doesn't accept. Reload the page and try again.");
  }
  return parts.slice(0, 3).join(" · ") + (parts.length > 3 ? ` (+${parts.length - 3} more)` : "");
}

// For forms with inline errors: toast text + per-field messages.
export function formError(e, labels = {}) {
  const fields = fieldErrors(e);
  const d = e?.response?.data?.detail;
  const other = Array.isArray(d) ? d.filter((x) => x?.type === "extra_forbidden" || !fieldOf(x)) : [];
  if (!Object.keys(fields).length) return { message: errorMessage(e, undefined, labels), fields };
  return { message: "Please fix the highlighted fields" + (other.length ? `. ${describe422(other, labels)}` : ""), fields };
}

export function errorMessage(e, fallback = "Something went wrong. Please try again.", labels = {}) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return describe422(detail, labels) || fallback;
  if (typeof detail.msg === "string") return detail.msg;
  return fallback;
}
